import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { auctionBidWinners } from "../api/ApiRoutesFile";
import { handleError } from "../api/errorHandler";

export const orderApiSlice = createApi({
  reducerPath: "orderApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "https://castle-auction-production.up.railway.app/api/",
    prepareHeaders: (headers) => {
      headers.set("Content-Type", "application/json");
      const token = localStorage.getItem("auction_portal");
      if (token) {
        headers.set("x-auth-token", token);
      }
      return headers;
    },
  }),
  tagTypes: ["Orders"],
  endpoints: (builder) => ({
    orders: builder.query({
      query: ({ endpoint, id, params }) => {
        const url = id ? `${endpoint}${id}` : endpoint;
        return {
          url,
          method: "GET",
          params: params || undefined,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.applications.map(({ _id }) => ({
                type: "Orders",
                id: _id,
              })),
              { type: "Orders", id: "LIST" },
            ]
          : [{ type: "Orders", id: "LIST" }],
    }),

    updateOrder: builder.mutation({
      query: ({ endpoint, id, formData }) => ({
        url: `${endpoint}/${id}`,
        method: "PUT",
        body: formData,
      }),
      async onQueryStarted(
        {
          endpoint,
          id,
          formData,
          lastId,
          auctionId,
          activeTab,
          originalStatus,
        },
        { dispatch, queryFulfilled, getState }
      ) {
        try {
          // Get the original status if it wasn't passed in
          let currentOriginalStatus = originalStatus;

          if (!currentOriginalStatus) {
            // Try to get original status from the cache
            const state = getState();
            const cacheKey = JSON.stringify({
              endpoint: auctionBidWinners,
              id: lastId,
              params: `auction=${auctionId}&status=winner&orderstatus=${
                activeTab === "all" ? "" : activeTab
              }`,
            });

            const queryData =
              state.orderApi.queries[`orders(${cacheKey})`]?.data;

            if (queryData?.applications) {
              const originalOrder = queryData.applications.find(
                (item) => item._id === id
              );
              if (originalOrder) {
                currentOriginalStatus = originalOrder.orderstatus;
              }
            }
          }

          // Get updated data from API
          const { data } = await queryFulfilled;
          if (!data?.order) return;

          const updatedOrder = data.order;
          const newOrderStatus = updatedOrder.orderstatus;

          // Handle different tabs based on the status changes

          // 1. Update current active tab
          dispatch(
            orderApiSlice.util.updateQueryData(
              "orders",
              {
                endpoint: auctionBidWinners,
                id: lastId,
                params: `auction=${auctionId}&status=winner&orderstatus=${
                  activeTab === "all" ? "" : activeTab
                }`,
              },
              (draft) => {
                if (!draft?.applications) return;

                // If we're in a specific tab and status changed to something else, remove the item
                if (activeTab !== "all" && newOrderStatus !== activeTab) {
                  draft.applications = draft.applications.filter(
                    (item) => item._id !== id
                  );
                } else {
                  // Otherwise update the item in place
                  const index = draft.applications.findIndex(
                    (item) => item._id === id
                  );
                  if (index !== -1) {
                    draft.applications[index] = updatedOrder;
                  }
                }
              }
            )
          );

          // 2. Handle status change - remove from original status tab and add to new status tab
          if (
            currentOriginalStatus &&
            newOrderStatus !== currentOriginalStatus
          ) {
            // Remove from original status tab
            dispatch(
              orderApiSlice.util.updateQueryData(
                "orders",
                {
                  endpoint: auctionBidWinners,
                  id: lastId,
                  params: `auction=${auctionId}&status=winner&orderstatus=${currentOriginalStatus}`,
                },
                (draft) => {
                  if (draft?.applications) {
                    draft.applications = draft.applications.filter(
                      (item) => item._id !== id
                    );
                  }
                }
              )
            );

            // Add to new status tab
            dispatch(
              orderApiSlice.util.updateQueryData(
                "orders",
                {
                  endpoint: auctionBidWinners,
                  id: lastId,
                  params: `auction=${auctionId}&status=winner&orderstatus=${newOrderStatus}`,
                },
                (draft) => {
                  if (draft?.applications) {
                    // Add at the beginning of the list
                    draft.applications.unshift(updatedOrder);
                  }
                }
              )
            );
          }

          // 3. Always update the "all" tab
          if (activeTab !== "all") {
            dispatch(
              orderApiSlice.util.updateQueryData(
                "orders",
                {
                  endpoint: auctionBidWinners,
                  id: lastId,
                  params: `auction=${auctionId}&status=winner&orderstatus=`,
                },
                (draft) => {
                  if (draft?.applications) {
                    const index = draft.applications.findIndex(
                      (item) => item._id === id
                    );
                    if (index !== -1) {
                      draft.applications[index] = updatedOrder;
                    }
                  }
                }
              )
            );
          }
        } catch (error) {
          handleError(error);
        }
      },
    }),
  }),
});

export const { useOrdersQuery, useUpdateOrderMutation } = orderApiSlice;
